import { ChevronDown } from 'lucide-react';
import type { CSSProperties, FC } from 'react';

import type { StorefrontMenuItem } from '@/features/storefront/api/storefront';
import { StorefrontContainer } from '@/features/storefront/components/StorefrontContainer';
import {
  HeaderBannerRail,
  HeaderCategoryLinks,
  MenuLink,
  MobileNav,
  StoreLogo,
} from '@/features/storefront/layouts/chrome/ChromeParts';
import { MegaColumns, useHoverDisclosure } from '@/features/storefront/layouts/chrome/MegaMenu';
import { FittingNav } from '@/features/storefront/layouts/chrome/FittingNav';
import { menuItems, type HeaderProps } from '@/features/storefront/layouts/chrome/types';
import { cn } from '@/lib/cn';

/** The masthead's navigation voice: the heading serif, small and spaced. */
const EMERAUDE_NAV = 'items-center gap-9 whitespace-nowrap text-[16px] tracking-[0.02em]';

const NAV_FONT: CSSProperties = { fontFamily: 'var(--storefront-heading-font)' };

/**
 * The rail's ground: the primary emerald rather than the text colour, by the
 * same variable swap Feast and Lumera make, so a merchant who repaints their
 * primary gets a rail that follows it.
 */
const RAIL_STYLE = {
  '--storefront-palette-text': 'var(--storefront-palette-primary)',
} as CSSProperties;

/** Whether an item opens a whole panel of columns rather than a short list. */
function isMega(item: StorefrontMenuItem): boolean {
  return (item.items ?? []).some((child) => (child.items ?? []).length > 0);
}

/**
 * One item on the bar, and whatever the merchant filed under it.
 *
 * Two shapes, chosen by the menu itself. An item whose children have children
 * of their own - "Shop" → Earrings → Jhumkas - opens a **mega panel** the
 * width of the page, one column per child with its own links under a serif
 * heading, which is how a jeweller's range is browsed: by kind, then by style.
 * An item with a flat list of children opens a small card under itself.
 *
 * The item stays a link either way; the panel is a shortcut. Opens on hover
 * and on focus, closes on Escape and when focus leaves it.
 */
function NavItem({ item, preview }: { item: StorefrontMenuItem; preview?: boolean }) {
  const { open, props } = useHoverDisclosure();
  const children = item.items ?? [];
  const mega = isMega(item);

  if (children.length === 0) {
    return (
      <MenuLink item={item} preview={preview} className="underline-offset-4 hover:underline" />
    );
  }

  return (
    <div
      className={cn(!mega && 'relative')}
      {...props}
    >
      <span className="flex h-full items-center gap-1 py-2">
        <MenuLink item={item} preview={preview} className="underline-offset-4 hover:underline" />
        <ChevronDown
          className={cn('size-3.5 transition-transform', open && 'rotate-180')}
          aria-hidden
        />
      </span>

      {open && mega ? (
        // Positioned against the header (the nearest positioned ancestor), so
        // it spans the page under the hairline rather than hanging off the item.
        <div
          className="absolute inset-x-0 top-full z-40 border-y shadow-xl"
          style={{
            backgroundColor: 'var(--storefront-background)',
            borderColor: 'var(--storefront-surface)',
          }}
        >
          <StorefrontContainer className="py-10">
            <MegaColumns
              linksClassName="gap-2.5 font-sans text-[14px]"
              columns={children.map((column) => ({
                key: `${column.label}:${column.url}`,
                heading: (
                  <MenuLink
                    item={column}
                    preview={preview}
                    className="mb-4 block text-xl hover:opacity-70"
                  />
                ),
                links: (column.items ?? []).map((link) => (
                  <MenuLink
                    key={`${link.label}:${link.url}`}
                    item={link}
                    preview={preview}
                    className="hover:opacity-60"
                  />
                )),
              }))}
            />
          </StorefrontContainer>
        </div>
      ) : null}

      {open && !mega ? (
        <div className="absolute left-1/2 top-full z-40 -translate-x-1/2 pt-2">
          <div
            className="min-w-52 border py-2 shadow-xl"
            style={{
              backgroundColor: 'var(--storefront-background)',
              borderColor: 'var(--storefront-surface)',
            }}
          >
            {children.map((child) => (
              <MenuLink
                key={`${child.label}:${child.url}`}
                item={child}
                preview={preview}
                className="block px-5 py-2 hover:opacity-60"
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * A jeweller's masthead: the emerald rail with the house's standing promises,
 * then one white deck closed by a hairline - wordmark left, the menu across the
 * middle in the heading serif, the shopper's corner right.
 *
 * Like the other families, the bar stands the shop's own shelves in when the
 * merchant has built no menu, and below the large breakpoint the menu moves
 * into the sheet behind the menu button.
 */
export const Header: FC<HeaderProps> = ({
  storeName,
  logoUrl,
  description,
  header,
  menu,
  endSlot,
  preview,
}) => {
  const items = menuItems(menu);

  return (
    <header
      className={cn('relative border-b', header.sticky && !preview && 'sticky top-0 z-40')}
      style={{
        backgroundColor: 'var(--storefront-background)',
        borderColor: 'color-mix(in srgb, var(--storefront-primary) 35%, transparent)',
      }}
    >
      <div style={RAIL_STYLE}>
        <HeaderBannerRail header={header} fallback={description} preview={preview} />
      </div>

      <StorefrontContainer className="flex min-h-[4.75rem] items-center gap-3 py-2 sm:gap-6 preview-phone:gap-3!">
        <MobileNav menu={menu} hiddenFrom="lg:hidden" preview={preview} categoryFallback />
        <StoreLogo
          storeName={storeName}
          logoUrl={logoUrl}
          logoWidthPx={header.logo_width_px}
          showStoreName={header.show_store_name}
          preview={preview}
          className="gap-2.5"
          // Sized down on a phone, where the full-size caps lose most of a
          // two-word name behind the menu button and the shopper's corner.
          nameClassName="text-lg uppercase leading-none tracking-[0.02em] sm:text-[1.75rem] sm:tracking-[0.06em] preview-phone:text-lg! preview-phone:tracking-[0.02em]!"
          nameStyle={{
            fontFamily: 'var(--storefront-heading-font)',
            color: 'var(--storefront-primary)',
          }}
        />

        <div
          className="hidden min-w-0 flex-1 justify-center self-stretch lg:flex preview-narrow:hidden!"
          style={NAV_FONT}
        >
          {items.length > 0 ? (
            <FittingNav
              items={items}
              label={menu?.name}
              preview={preview}
              navClassName={EMERAUDE_NAV}
              moreClassName="underline-offset-4 hover:underline"
              renderItem={(item) => <NavItem key={`${item.label}:${item.url}`} item={item} preview={preview} />}
            />
          ) : preview ? null : (
            <HeaderCategoryLinks
              className={cn('flex', EMERAUDE_NAV)}
              linkClassName="underline-offset-4 hover:underline"
              header={header}
              limit={6}
            />
          )}
        </div>

        <div className="ms-auto flex shrink-0 items-center gap-4 sm:gap-5">{endSlot}</div>
      </StorefrontContainer>
    </header>
  );
};
